import React from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/layout/Shell';
import { Button } from '../components/ui/button';
import {
  Target, ChartBar, Sparkle, Warning, Scales, ArrowRight,
} from '@phosphor-icons/react';

const Section = ({ title, eyebrow, children, testId }) => (
  <section className="mb-12" data-testid={testId}>
    {eyebrow && <div className="label-eyebrow text-primary mb-2">{eyebrow}</div>}
    <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-4">
      {title}
    </h2>
    <div className="prose prose-neutral max-w-none text-sm lg:text-base leading-relaxed text-foreground space-y-3">
      {children}
    </div>
  </section>
);

const Methodology = () => {
  return (
    <Shell>
      <div className="max-w-[820px] mx-auto px-6 py-16" data-testid="methodology-page">
        <div className="mb-12">
          <div className="label-eyebrow text-primary mb-3 flex items-center gap-2">
            <Target size={14} weight="fill" /> METHODOLOGY
          </div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-4">
            How Hikari really scores people.
          </h1>
          <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
            No black box. Here's the entire scoring methodology, the role-family weights,
            the anti-gaming safeguards, and where we know the engine has limits.
          </p>
        </div>

        <Section title="The four sub-scores" eyebrow="1 · WHAT WE MEASURE" testId="meth-subscores">
          <p>
            Every candidate is graded on four independent sub-scores, each on a 0–100 scale.
            They measure different things, on purpose.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Performance</strong> — how well you do on role-specific assessments
              (MCQ, written, scenario, timed-task, artifact). This is the closest analogue to a
              traditional skills test.
            </li>
            <li>
              <strong>Consistency</strong> — how stable your performance is across multiple
              attempts and assessment types. A candidate who scores 92 on three different
              assessments is more reliable than one who scores 100 on one and 60 on the next.
            </li>
            <li>
              <strong>Reliability</strong> — composite of your work history signals — verified
              credentials, past tenure, peer endorsements where available. This is the only
              sub-score where prior employment matters.
            </li>
            <li>
              <strong>Artifacts</strong> — what you've actually built. Projects, portfolio pieces,
              published work, side businesses. The proof you bring with you.
            </li>
          </ul>
        </Section>

        <Section title="How the overall fit is built" eyebrow="2 · THE WEIGHTED SUM" testId="meth-weights">
          <p>
            Your overall fit (the big number) is a weighted sum of the four sub-scores.
            The weights vary by role family. Below are the actual defaults we ship with:
          </p>
          <div className="border border-border my-4 overflow-x-auto">
            <table className="w-full mono text-xs">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left p-2 label-eyebrow">Role family example</th>
                  <th className="p-2 label-eyebrow">P</th>
                  <th className="p-2 label-eyebrow">C</th>
                  <th className="p-2 label-eyebrow">R</th>
                  <th className="p-2 label-eyebrow">A</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="p-2">Software Engineering</td>
                  <td className="text-center p-2">0.50</td>
                  <td className="text-center p-2">0.25</td>
                  <td className="text-center p-2">0.10</td>
                  <td className="text-center p-2">0.15</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="p-2">Nursing (RN / PSW)</td>
                  <td className="text-center p-2">0.30</td>
                  <td className="text-center p-2">0.25</td>
                  <td className="text-center p-2">0.35</td>
                  <td className="text-center p-2">0.10</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="p-2">Graphic & Visual Design</td>
                  <td className="text-center p-2">0.25</td>
                  <td className="text-center p-2">0.15</td>
                  <td className="text-center p-2">0.10</td>
                  <td className="text-center p-2">0.50</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="p-2">Operations Management</td>
                  <td className="text-center p-2">0.40</td>
                  <td className="text-center p-2">0.25</td>
                  <td className="text-center p-2">0.20</td>
                  <td className="text-center p-2">0.15</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="p-2">Customer Support</td>
                  <td className="text-center p-2">0.45</td>
                  <td className="text-center p-2">0.30</td>
                  <td className="text-center p-2">0.15</td>
                  <td className="text-center p-2">0.10</td>
                </tr>
                <tr className="border-t border-border bg-accent/20">
                  <td className="p-2 font-bold">Early-career mode (any family)</td>
                  <td className="text-center p-2 font-bold">0.50</td>
                  <td className="text-center p-2 font-bold">0.30</td>
                  <td className="text-center p-2 font-bold">0.00</td>
                  <td className="text-center p-2 font-bold">0.20</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mono text-xs text-muted-foreground">
            Every role family's exact weights are visible on the employer's Why panel — we
            never hide them. P = Performance, C = Consistency, R = Reliability, A = Artifacts.
          </p>
        </Section>

        <Section title="Why role-specific weights matter" eyebrow="3 · CONTEXT" testId="meth-context">
          <p>
            A nurse and a graphic designer don't share the same definition of "good". A nurse
            who hasn't held a job for two years is a red flag; a designer who hasn't held a
            job for two years might have been building a portfolio. Weights let us honour that.
          </p>
          <p>
            And for candidates who don't have a work history yet — students, returners, teens —
            we offer <strong>Early-career mode</strong>: Reliability drops to zero, Performance
            and Consistency carry 80% of the score, and your assessments + projects do the rest.
            You opt in from your profile.
          </p>
        </Section>

        <Section title={`The "Why this candidate?" panel`} eyebrow="4 · TRANSPARENCY" testId="meth-why">
          <p>
            Every employer who sees a score also sees:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>The four sub-score bars and a radar plot</li>
            <li>The exact weights used for this role</li>
            <li>A plain-English explanation (written by Claude, audit-logged)</li>
            <li>Matched signals (skills, specialization, work-authorization, location) — colour-coded</li>
            <li>Gaps to probe in interview — never framed as flaws</li>
            <li>Top 3 proof points from the candidate's projects, with skill overlap highlighted</li>
            <li>A work-style summary</li>
            <li>Integrity flags (if any) — AI-writing detection, cross-attempt duplicates</li>
            <li>Every assessment considered, with raw score, duration, and submitted date</li>
          </ul>
          <p>
            If the employer can't defend the score from this panel alone, our methodology has failed.
            We treat that as a bug, not a feature.
          </p>
        </Section>

        <Section title="Anti-gaming" eyebrow="5 · INTEGRITY" testId="meth-anti-gaming">
          <ul className="list-disc pl-5 space-y-1">
            <li>Every written answer is screened by Claude for AI-generation patterns; flagged ones surface on the Why panel.</li>
            <li>Duplicate-text detection across attempts catches copy-paste rings.</li>
            <li>Time-on-task is logged. Anomalously fast or slow attempts are flagged.</li>
            <li>Assessment items are auto-shuffled. Question banks are rotated periodically.</li>
            <li>Cheating attempts <em>aren't</em> deletions — they're flags. Employers decide what to do with them.</li>
          </ul>
        </Section>

        <Section title="Fairness audits" eyebrow="6 · CHECKING OURSELVES" testId="meth-fairness">
          <p>
            We run an internal disparity audit on our scores by country of residence and work
            authorization (gated to populations of n ≥ 3 to protect individuals). Materially
            different fit-score distributions across groups are surfaced to our team in
            <code> /admin/fairness</code>. The point isn't to force equal averages — it's to make
            sure we can <em>see</em> when something feels off and react.
          </p>
          <p>
            We commit to publishing a fairness summary once a year, starting in 2027 once we have
            enough data to make the comparisons meaningful.
          </p>
        </Section>

        <Section title="What we measure poorly (and won't pretend otherwise)" eyebrow="7 · LIMITS" testId="meth-limits">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Soft skills under pressure.</strong> Our work-style profile gives a rough
              read, but you can't fully infer how someone handles a crisis from a written test.
              Interviews still matter.
            </li>
            <li>
              <strong>Cultural fit.</strong> We deliberately don't score it. Culture is your call.
              Hikari ranks capability; the human in the chair ranks fit.
            </li>
            <li>
              <strong>Domain-specific certifications we don't recognise yet.</strong> If your
              field uses a credential we haven't catalogued, it shows up as "credential" but
              not auto-verified. We're expanding the registry continuously.
            </li>
            <li>
              <strong>Anything we shouldn't measure.</strong> Race, religion, gender, age,
              sexual orientation. We don't collect these and never will.
            </li>
          </ul>
        </Section>

        <Section title="Versioning" eyebrow="8 · THIS WILL CHANGE" testId="meth-versioning">
          <p>
            Hikari's methodology is on version 2 (February 2026). Every change goes through
            an internal review and is logged in <code>/admin/methodology-changelog</code>
            (coming soon). When we change weights, we email affected candidates and employers
            at least 14 days before the change ships.
          </p>
        </Section>

        {/* CTA */}
        <div className="bg-foreground text-background p-8 mb-12 text-center">
          <div className="label-eyebrow text-accent mb-2">SEE IT IN ACTION</div>
          <div className="font-display font-black text-2xl lg:text-3xl tracking-tighter mb-4">
            Read about it, or watch it work.
          </div>
          <div className="flex items-center justify-center gap-2">
            <Link to="/sandbox">
              <Button className="rounded-none bg-accent text-foreground hover:bg-accent/90">
                See a live scored candidate <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="rounded-none bg-transparent border-background/40 text-background hover:bg-background hover:text-foreground">
                Create an account
              </Button>
            </Link>
          </div>
        </div>

        {/* Footer nav */}
        <div className="border-t border-border pt-8 flex items-center justify-between flex-wrap gap-4">
          <Link to="/" className="label-eyebrow text-muted-foreground hover:text-foreground">
            ← Back to home
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/privacy" className="label-eyebrow text-muted-foreground hover:text-foreground">
              Privacy →
            </Link>
            <Link to="/security" className="label-eyebrow text-muted-foreground hover:text-foreground">
              Security →
            </Link>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default Methodology;
